import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, ChevronDown, ChevronUp, Zap, Flame, Server } from "lucide-react";
import { useCMS, useSectionEnabled } from "../context/CMSContext";
import { trackPlanClick } from "../lib/tracking";

const PLAN_ICONS = {
  // legacy keys (kept so old bookmarks/links don't break visually)
  spark: Zap, ignite: Flame, blaze: Server,
  // current keys
  start: Zap, scale: Flame, infinity: Server,
};

function PlanCardDesktop({ p, symbol, onClick }) {
  const dark = p.popular;
  return (
    <div
      className={`relative rounded-3xl p-7 sm:p-8 ${
        dark ? "lm-dark-card shadow-xl" : "bg-white border border-[#11151C]/10"
      } transition-transform hover:-translate-y-1`}
    >
      {dark && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 lm-popular-ribbon px-3 py-1 rounded-full text-xs inline-flex items-center gap-1">
          <Crown className="w-3.5 h-3.5" /> Most Popular
        </span>
      )}
      <h3 className={`lm-font font-extrabold text-2xl ${dark ? "text-white" : "text-[#11151C]"}`}>
        {p.name}
      </h3>
      <p className={`mt-1 text-sm ${dark ? "text-white/70" : "text-[#11151C]/60"}`}>{p.tagline}</p>

      <div className="mt-6 flex items-baseline gap-2">
        {p.oldPrice && (
          <span className={`text-sm line-through ${dark ? "text-white/40" : "text-[#11151C]/40"}`}>
            {p.oldPrice}
          </span>
        )}
      </div>
      <div className="flex items-baseline mt-1">
        <span className={`text-2xl font-bold ${dark ? "text-white" : "text-[#11151C]"}`}>{symbol}</span>
        <span className={`lm-font font-extrabold text-5xl sm:text-6xl leading-none ${dark ? "text-white" : "text-[#11151C]"}`}>
          {p.price}
        </span>
        <span className={`ml-1 text-lg ${dark ? "text-white/70" : "text-[#11151C]/60"}`}>{p.period}</span>
      </div>
      <p className={`text-xs mt-2 ${dark ? "text-white/60" : "text-[#11151C]/50"}`}>{p.renew}</p>

      <button
        onClick={onClick}
        className={`mt-6 w-full py-3.5 rounded-xl font-bold text-sm ${dark ? "lm-btn-accent" : "lm-btn-primary"}`}
      >
        {p.ctaText || "Choose plan"}
      </button>

      <ul className="mt-6 space-y-3">
        {(p.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <span
              className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                dark ? "bg-[#FF6B3D]" : "bg-[#FFE6D6]"
              }`}
            >
              <Check className={`w-3 h-3 ${dark ? "text-[#11151C]" : "text-[#B23B12]"}`} strokeWidth={3} />
            </span>
            <span className={`text-sm ${dark ? "text-white/85" : "text-[#11151C]/80"}`}>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanCardMobile({ p, symbol, onClick }) {
  const dark = p.popular;
  const [open, setOpen] = useState(false);
  const Icon = PLAN_ICONS[p.key] || Zap;
  const highlights = (p.features || []).slice(0, 3);

  return (
    <div className={`relative rounded-2xl p-5 ${dark ? "lm-dark-card" : "bg-white border border-[#11151C]/10"}`}>
      {dark && (
        <span className="absolute -top-2.5 right-4 lm-popular-ribbon px-2.5 py-0.5 rounded-full text-[10px] inline-flex items-center gap-1">
          <Crown className="w-3 h-3" /> Most Popular
        </span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-white/10" : "bg-[#FFE6D6]"}`}>
            <Icon className={`w-5 h-5 ${dark ? "text-[#FF6B3D]" : "text-[#B23B12]"}`} />
          </span>
          <div>
            <h3 className={`lm-font font-extrabold text-lg ${dark ? "text-white" : "text-[#11151C]"}`}>{p.name}</h3>
            <p className={`text-xs ${dark ? "text-white/60" : "text-[#11151C]/55"}`}>{p.tagline}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {p.oldPrice && (
            <div className={`text-xs line-through ${dark ? "text-white/40" : "text-[#11151C]/40"}`}>{p.oldPrice}</div>
          )}
          <div className="flex items-baseline justify-end">
            <span className={`text-base font-bold ${dark ? "text-white" : "text-[#11151C]"}`}>{symbol}</span>
            <span className={`lm-font font-extrabold text-3xl leading-none ${dark ? "text-white" : "text-[#11151C]"}`}>
              {p.price}
            </span>
            <span className={`text-xs ml-0.5 ${dark ? "text-white/60" : "text-[#11151C]/55"}`}>{p.period}</span>
          </div>
          <div className={`text-[10px] mt-0.5 ${dark ? "text-white/50" : "text-[#11151C]/45"}`}>{p.renew}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {highlights.map((h) => (
          <span
            key={h}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
              dark ? "bg-white/10 text-white/85" : "bg-[#FFE6D6] text-[#B23B12]"
            }`}
          >
            {h}
          </span>
        ))}
      </div>

      <button
        onClick={() => setOpen(!open)}
        className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${
          dark ? "text-white/80" : "text-[#11151C]/80"
        }`}
      >
        {open ? "Hide features" : "See all features"}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <ul className="mt-3 space-y-2">
          {(p.features || []).map((f) => (
            <li key={f} className="flex items-start gap-2">
              <span
                className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  dark ? "bg-[#FF6B3D]" : "bg-[#FFE6D6]"
                }`}
              >
                <Check className={`w-2.5 h-2.5 ${dark ? "text-[#11151C]" : "text-[#B23B12]"}`} strokeWidth={3} />
              </span>
              <span className={`text-xs ${dark ? "text-white/85" : "text-[#11151C]/80"}`}>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={onClick}
        className={`mt-5 w-full py-3 rounded-xl font-bold text-sm ${dark ? "lm-btn-accent" : "lm-btn-primary"}`}
      >
        {p.ctaText || "Choose plan"}
      </button>
    </div>
  );
}

export default function Pricing() {
  const enabled = useSectionEnabled("pricing");
  const { cms } = useCMS();
  const navigate = useNavigate();
  if (!enabled) return null;
  const plans = cms?.plans || [];
  const symbol = cms?.site?.currencySymbol || "\u20b9";
  const sec = cms?.pricing_section || {};

  const handleClick = (p) => {
    // Fire AddToCart on FB Pixel + GTM dataLayer
    try {
      const price = parseFloat(p.price || (p.pricing_tiers?.[0]?.price) || 0) || 0;
      trackPlanClick({ plan_key: p.key, plan_name: p.name, price, months: 12 });
    } catch (_) { /* never let tracking break navigation */ }

    const url = p.ctaUrl || `/checkout?plan=${p.key}`;
    if (url.startsWith("http")) {
      window.location.href = url;
    } else if (url.startsWith("/")) {
      navigate(url);
    } else {
      const el = document.querySelector(url);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="pricing" className="bg-white py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <span className="lm-chip-accent inline-block px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase">
            {sec.eyebrow || "Pricing & Plans"}
          </span>
          <h2 className="lm-section-title mt-4 text-2xl sm:text-4xl lg:text-5xl">
            {sec.title || "Simple, Transparent Pricing"}
          </h2>
          <p className="text-[#11151C]/65 mt-3 text-base sm:text-lg">
            {sec.subtitle || "All plans include 30-day money back guarantee"}
          </p>
        </div>

        {/* Desktop */}
        <div className="hidden lg:grid lg:grid-cols-3 gap-5">
          {plans.map((p) => (
            <PlanCardDesktop key={p.key || p.name} p={p} symbol={symbol} onClick={() => handleClick(p)} />
          ))}
        </div>

        {/* Mobile/Tablet */}
        <div className="lg:hidden space-y-4">
          {plans.map((p) => (
            <PlanCardMobile key={p.key || p.name} p={p} symbol={symbol} onClick={() => handleClick(p)} />
          ))}
        </div>
      </div>
    </section>
  );
}
